import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { Router } from "wouter";
import { useBrowserLocation, useSearch } from "wouter/use-browser-location";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import NotFound from "@/pages/not-found";
import { Button } from "@/components/ui/button";
import { globalTemplateStudioPath } from "./lp-template-studio-routing";
import {
  brandUrlNamesEligible, projectBrandUrl, UUID_URL_TOKEN, BRAND_URL_RESERVED_NAMES,
  type BrandUrlIdentity,
} from "@shared/brand-url-name";

export type DirectoryBrand = BrandUrlIdentity & { workspaceId: string; name?: string; logoUrl?: string | null };
type RoutingState = { enabled: boolean; brands: readonly DirectoryBrand[] };
const RoutingContext = createContext<RoutingState>({ enabled: false, brands: [] });
export function useBrandUrlNames() { return useContext(RoutingContext); }

async function read<T>(url: string): Promise<T> {
  const response = await fetch(url, { credentials: "include", cache: "no-store" });
  if (!response.ok) throw new Error("Could not load brand access. Try again.");
  return response.json();
}

// Hook identity is stable; live mappings are supplied through context.
function useInternalLocation(options?: { ssrPath?: string }): ReturnType<typeof useBrowserLocation> {
  const [path, navigate] = useBrowserLocation(options);
  const { enabled, brands } = useBrandUrlNames();
  const go = useCallback<typeof navigate>((to, options) => {
    const target = String(to);
    navigate(enabled ? projectBrandUrl(target, brands, true) : target, options);
  }, [navigate, enabled, brands]);
  return [enabled ? projectBrandUrl(path, brands, false) : path, go];
}

export function reconcileBrandUrlNameCache(client: ReturnType<typeof useQueryClient>, brand: DirectoryBrand) {
  client.setQueriesData({ predicate: q => typeof q.queryKey[0] === "string" &&
    q.queryKey[0].startsWith("/api/brands?workspaceId=") }, (old: any) =>
      Array.isArray(old) ? old.map(b => b.id === brand.id ? { ...b, ...brand } : b) : old);
  client.setQueriesData({ predicate: q =>
    (q.queryKey[0] === "/api/brands" && q.queryKey[1] === brand.id) ||
    q.queryKey[0] === `/api/brands/${brand.id}` }, (old: any) => old ? { ...old, ...brand } : old);
}

export function BrandUrlRouting({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [browserPath, navigate] = useBrowserLocation();
  useSearch();
  const client = useQueryClient();
  const [accountScope, setAccountScope] = useState(user?.id ?? null);
  const accountChanged = accountScope !== (user?.id ?? null);
  useLayoutEffect(() => {
    if (!accountChanged) return;
    // Shared legacy caches can contain directory responses seeded by the
    // previous account. Clear them before painting another account's pages.
    client.removeQueries({ predicate: q => {
      const path = q.queryKey[0];
      if (typeof path !== "string") return false;
      if (path === "/api/workspaces") return q.queryKey.length === 1 || q.queryKey[1] !== user?.id;
      if (path.startsWith("/api/brands?workspaceId=")) return q.queryKey[1] !== user?.id;
      return path === "/api/brands" || path.startsWith("/api/brands/") || path === "/api/admin/brands";
    } });
    setAccountScope(user?.id ?? null);
  }, [accountChanged, user?.id, client]);
  const legacyStudio = globalTemplateStudioPath(browserPath);
  const enabled = brandUrlNamesEligible(user) && !legacyStudio;
  const workspaces = useQuery<{ id: string }[]>({
    queryKey: ["/api/workspaces", user?.id],
    queryFn: () => read("/api/workspaces"), enabled, staleTime: 0, retry: false,
  });
  const lists = useQueries({ queries: (enabled ? workspaces.data ?? [] : []).map(w => ({
    queryKey: [`/api/brands?workspaceId=${w.id}`, user?.id],
    queryFn: () => read<DirectoryBrand[]>(`/api/brands?workspaceId=${w.id}`),
    staleTime: 0, retry: false,
  })) });
  const failed = enabled && (workspaces.isError || lists.some(q => q.isError));
  const loading = enabled && (workspaces.isPending || lists.some(q => q.isPending));
  const brands = useMemo(() => enabled && !failed && !loading ? lists.flatMap(q => q.data ?? []) : [],
    [enabled, failed, loading, user?.id,
      lists.flatMap(q => q.data ?? []).map(b => JSON.stringify([b.id, b.urlName, b.name, b.logoUrl])).join("|")]);
  const hrefs = useCallback((href: string) => enabled ? projectBrandUrl(href, brands, true) : href, [enabled, brands]);
  const match = legacyStudio ? null : browserPath.match(/^\/brands\/([^/]+)(?:\/|$)/);
  const isSlug = Boolean(match && !UUID_URL_TOKEN.test(match[1]) && !BRAND_URL_RESERVED_NAMES.has(match[1]));

  useEffect(() => {
    if (!enabled || failed || loading || legacyStudio) return;
    // Seed the existing list caches only with authenticated, account-scoped
    // responses. Never build the directory from a stale first-workspace cache.
    lists.forEach((query, index) => {
      const workspaceId = workspaces.data?.[index]?.id;
      if (workspaceId && query.data) client.setQueryData([`/api/brands?workspaceId=${workspaceId}`], query.data);
    });
    const canonical = projectBrandUrl(projectBrandUrl(browserPath, brands, false), brands, true);
    if (canonical !== browserPath) navigate(canonical + window.location.search + window.location.hash,
      { replace: true, state: window.history.state });
  }, [enabled, failed, loading, legacyStudio, brands, browserPath, client, navigate]);

  if (match && (accountChanged || (enabled && loading))) {
    return <div role="status" className="p-8">Loading brand access...</div>;
  }
  if (user && match && failed) {
    return <div role="alert" className="p-8 space-y-3"><p>Could not load brand access.</p>
      <Button onClick={() => { void workspaces.refetch(); lists.forEach(q => void q.refetch()); }}>Try again</Button></div>;
  }
  if (user && isSlug && (!enabled || !brands.some(b => b.urlName === match![1].toLowerCase()))) return <NotFound />;
  return <RoutingContext.Provider value={{ enabled, brands }}>
    <Router hook={useInternalLocation} hrefs={hrefs}>{children}</Router>
  </RoutingContext.Provider>;
}
